import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  getPurgeConfig,
  updatePurgeConfig,
  getPurgeStats,
  getPurgeHistory,
  getEligibleItems,
  executeDryRun,
  executePurge,
  getPurgeJobStatus,
  startPurgeJob,
  stopPurgeJob
} from '../api/purge';
import {
  Settings,
  BarChart3,
  Clock,
  Trash2,
  Play,
  StopCircle,
  FileText,
  AlertTriangle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import LoadingSpinner from '../components/common/LoadingSpinner';

const PurgeManagementPage = () => {
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [eligibleItems, setEligibleItems] = useState([]);
  const [totalEligible, setTotalEligible] = useState(0);
  const [jobStatus, setJobStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('configuration');
  const [isCustomSchedule, setIsCustomSchedule] = useState(false);

  // Pagination state for eligible items
  const [eligiblePage, setEligiblePage] = useState(1);
  const [eligibleLimit] = useState(50);

  const [formData, setFormData] = useState({
    enabled: true,
    retentionDays: 90,
    batchSize: 100,
    schedule: '0 2 * * *',
    notifyBeforeHours: 24,
    notificationEmails: []
  });

  useEffect(() => {
    loadData();
  }, []);

  // Reload eligible items when page changes
  useEffect(() => {
    if (activeTab === 'eligible') {
      fetchEligibleItems();
    }
  }, [eligiblePage, activeTab]);

  const fetchEligibleItems = async () => {
    try {
      const offset = (eligiblePage - 1) * eligibleLimit;
      const res = await getEligibleItems(formData.retentionDays, eligibleLimit, offset);
      setEligibleItems(res.data.items || []);
      setTotalEligible(res.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch eligible items:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [configRes, statsRes, historyRes, statusRes] = await Promise.all([
        getPurgeConfig(),
        getPurgeStats(),
        getPurgeHistory({ limit: 10 }),
        getPurgeJobStatus()
      ]);

      setConfig(configRes.data);
      setFormData(configRes.data);
      setStats(statsRes.data);
      setHistory(historyRes.data || []);
      setJobStatus(statusRes.data);

      // Initial fetch for eligible items
      await fetchEligibleItems();
    } catch (error) {
      toast.error('Failed to load purge management data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await updatePurgeConfig(formData);
      toast.success('Configuration updated successfully');
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to update configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleDryRun = async () => {
    try {
      const result = await executeDryRun();
      toast.info(`Dry run complete: ${result.data.itemsEligible} items would be purged`, {
        autoClose: 5000
      });
    } catch (error) {
      toast.error('Dry run failed');
    }
  };

  const handleExecutePurge = async () => {
    if (
      !window.confirm(
        'Are you sure you want to execute the purge? This will permanently delete eligible items.'
      )
    ) {
      return;
    }
    try {
      const result = await executePurge();
      toast.success(`Purge completed: ${result.data.items_deleted} items permanently deleted`, {
        autoClose: 5000
      });
      loadData();
    } catch (error) {
      toast.error('Purge execution failed');
    }
  };

  const handleStartJob = async () => {
    try {
      await startPurgeJob();
      toast.success('Auto-purge job started');
      loadData();
    } catch (error) {
      toast.error('Failed to start job');
    }
  };

  const handleStopJob = async () => {
    try {
      await stopPurgeJob();
      toast.success('Auto-purge job stopped');
      loadData();
    } catch (error) {
      toast.error('Failed to stop job');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (ms) => {
    if (!ms) return '-';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    }
    return `${seconds}s`;
  };

  const totalPages = Math.ceil(totalEligible / eligibleLimit);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Settings className="h-7 w-7 mr-2 text-gray-600" />
            Purge Management
          </h1>
          <p className="text-sm text-gray-500">Configure automatic purging of deleted items</p>
        </div>

        {/* Job Status Badge */}
        {jobStatus && (
          <div className="flex items-center gap-2">
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                jobStatus.isScheduled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}
            >
              {jobStatus.isScheduled ? 'Active' : 'Inactive'}
            </span>
            {jobStatus.isRunning && (
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                Running
              </span>
            )}
          </div>
        )}
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4 border-2 border-blue-500">
            <p className="text-sm text-gray-500">Total Runs</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total_runs || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-2 border-green-500">
            <p className="text-sm text-gray-500">Items Purged</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total_deleted || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-2 border-yellow-500">
            <p className="text-sm text-gray-500">Avg Duration</p>
            <p className="text-2xl font-bold text-gray-900">
              {formatDuration(stats.avg_duration_ms)}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-2 border-orange-500">
            <p className="text-sm text-gray-500">Eligible Items</p>
            <p className="text-2xl font-bold text-gray-900">{totalEligible || 0}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('configuration')}
              className={`px-6 py-3 text-sm font-medium border-b-2 ${
                activeTab === 'configuration'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Configuration
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`px-6 py-3 text-sm font-medium border-b-2 ${
                activeTab === 'history'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              History
            </button>
            <button
              onClick={() => setActiveTab('eligible')}
              className={`px-6 py-3 text-sm font-medium border-b-2 ${
                activeTab === 'eligible'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Eligible Items
            </button>
          </nav>
        </div>

        {/* Configuration Tab */}
        {activeTab === 'configuration' && (
          <form onSubmit={handleSaveConfig} className="p-6">
            <div className="space-y-6">
              {/* Enable/Disable */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700">Auto-Purge Status</label>
                  <p className="text-xs text-gray-500">Enable or disable automatic purging</p>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, enabled: !formData.enabled })}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                    formData.enabled ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      formData.enabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {/* Retention Days */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Retention Period (Days)
                </label>
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={formData.retentionDays}
                  onChange={(e) =>
                    setFormData({ ...formData, retentionDays: parseInt(e.target.value) })
                  }
                  className="w-full max-w-xs border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Items older than this will be eligible for purging
                </p>
              </div>

              {/* Batch Size */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Batch Size</label>
                <input
                  type="number"
                  min="10"
                  max="1000"
                  value={formData.batchSize}
                  onChange={(e) =>
                    setFormData({ ...formData, batchSize: parseInt(e.target.value) })
                  }
                  className="w-full max-w-xs border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Number of items to purge per batch</p>
              </div>

              {/* Schedule */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Purge Schedule
                </label>
                <select
                  value={isCustomSchedule ? 'custom' : formData.schedule}
                  onChange={(e) => {
                    if (e.target.value === 'custom') {
                      setIsCustomSchedule(true);
                    } else {
                      setIsCustomSchedule(false);
                      setFormData({ ...formData, schedule: e.target.value });
                    }
                  }}
                  className="w-full max-w-md border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="0 2 * * *">Daily at 2:00 AM</option>
                  <option value="0 3 * * *">Daily at 3:00 AM</option>
                  <option value="0 0 * * *">Daily at Midnight</option>
                  <option value="0 2 * * 0">Weekly (Sunday at 2:00 AM)</option>
                  <option value="0 2 * * 1">Weekly (Monday at 2:00 AM)</option>
                  <option value="0 2 1 * *">Monthly (1st day at 2:00 AM)</option>
                  <option value="custom">Custom Schedule...</option>
                </select>

                {isCustomSchedule && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Custom Cron Expression
                    </label>
                    <input
                      type="text"
                      value={formData.schedule}
                      onChange={(e) => setFormData({ ...formData, schedule: e.target.value })}
                      placeholder="e.g., 0 4 * * * (daily at 4 AM)"
                      className="w-full max-w-md border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Format: minute hour day month weekday (e.g., 0 2 * * * = daily at 2 AM)
                    </p>
                  </div>
                )}

                {!isCustomSchedule && (
                  <p className="text-xs text-gray-500 mt-1">When the auto-purge job should run</p>
                )}
              </div>

              {/* Notification Hours */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notification Hours Before Purge
                </label>
                <input
                  type="number"
                  min="0"
                  max="72"
                  value={formData.notifyBeforeHours}
                  onChange={(e) =>
                    setFormData({ ...formData, notifyBeforeHours: parseInt(e.target.value) })
                  }
                  className="w-full max-w-xs border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400"
                >
                  <Settings className="h-5 w-5 mr-2" />
                  {saving ? 'Saving...' : 'Save Configuration'}
                </button>

                <button
                  type="button"
                  onClick={handleDryRun}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <FileText className="h-5 w-5 mr-2" />
                  Dry Run
                </button>

                <button
                  type="button"
                  onClick={handleExecutePurge}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                >
                  <Trash2 className="h-5 w-5 mr-2" />
                  Execute Purge Now
                </button>

                {jobStatus && !jobStatus.isScheduled && (
                  <button
                    type="button"
                    onClick={handleStartJob}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                  >
                    <Play className="h-5 w-5 mr-2" />
                    Start Job
                  </button>
                )}

                {jobStatus && jobStatus.isScheduled && (
                  <button
                    type="button"
                    onClick={handleStopJob}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700"
                  >
                    <StopCircle className="h-5 w-5 mr-2" />
                    Stop Job
                  </button>
                )}
              </div>

              {/* Job Status Info */}
              {jobStatus && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Job Status</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Status:</span>
                      <span className="ml-2 font-medium">
                        {jobStatus.isScheduled ? 'Scheduled' : 'Not Scheduled'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Running:</span>
                      <span className="ml-2 font-medium">{jobStatus.isRunning ? 'Yes' : 'No'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Last Run:</span>
                      <span className="ml-2 font-medium">{formatDate(jobStatus.lastRun)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Next Run:</span>
                      <span className="ml-2 font-medium">{formatDate(jobStatus.nextRun)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </form>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="p-6">
            {history.length === 0 ? (
              <div className="text-center py-12">
                <Clock className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No purge history</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Purge operations will appear here once executed
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Processed
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Deleted
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Failed
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Duration
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Type
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {history.map((entry) => (
                      <tr key={entry.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(entry.start_time)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {entry.items_processed}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                          {entry.items_deleted}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">
                          {entry.items_failed}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDuration(entry.duration_ms)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {entry.dry_run ? (
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                              Dry Run
                            </span>
                          ) : (
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                              Executed
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Eligible Items Tab */}
        {activeTab === 'eligible' && (
          <div className="p-6">
            {eligibleItems.length === 0 ? (
              <div className="text-center py-12">
                <Trash2 className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No eligible items</h3>
                <p className="mt-1 text-sm text-gray-500">
                  No items are currently eligible for purging
                </p>
              </div>
            ) : (
              <>
                <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800">
                      {totalEligible} items eligible for purging
                    </p>
                    <p className="text-xs text-yellow-700 mt-1">
                      These items have been in the recycle bin for more than{' '}
                      {formData.retentionDays} days and will be permanently deleted in the next
                      purge.
                    </p>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Entity Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Entity ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Deleted At
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Days in Bin
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {eligibleItems.map((item, index) => {
                        const daysInBin = Math.floor(
                          (new Date() - new Date(item.deleted_at)) / (1000 * 60 * 60 * 24)
                        );
                        return (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {item.entity_type}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {item.entity_id}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(item.deleted_at)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {daysInBin} days
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-4">
                    <div className="flex flex-1 justify-between sm:hidden">
                      <button
                        onClick={() => setEligiblePage(Math.max(1, eligiblePage - 1))}
                        disabled={eligiblePage === 1}
                        className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:bg-gray-100"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setEligiblePage(Math.min(totalPages, eligiblePage + 1))}
                        disabled={eligiblePage === totalPages}
                        className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:bg-gray-100"
                      >
                        Next
                      </button>
                    </div>
                    <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-gray-700">
                          Showing{' '}
                          <span className="font-medium">
                            {(eligiblePage - 1) * eligibleLimit + 1}
                          </span>{' '}
                          to{' '}
                          <span className="font-medium">
                            {Math.min(eligiblePage * eligibleLimit, totalEligible)}
                          </span>{' '}
                          of <span className="font-medium">{totalEligible}</span> results
                        </p>
                      </div>
                      <div>
                        <nav
                          className="isolate inline-flex -space-x-px rounded-md shadow-sm"
                          aria-label="Pagination"
                        >
                          <button
                            onClick={() => setEligiblePage(Math.max(1, eligiblePage - 1))}
                            disabled={eligiblePage === 1}
                            className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:bg-gray-100"
                          >
                            <span className="sr-only">Previous</span>
                            <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                          </button>

                          {/* Page numbers would go here, simplified for now */}
                          <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 focus:outline-offset-0">
                            Page {eligiblePage} of {totalPages}
                          </span>

                          <button
                            onClick={() => setEligiblePage(Math.min(totalPages, eligiblePage + 1))}
                            disabled={eligiblePage === totalPages}
                            className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:bg-gray-100"
                          >
                            <span className="sr-only">Next</span>
                            <ChevronRight className="h-5 w-5" aria-hidden="true" />
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PurgeManagementPage;
